import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import { usersAPI } from '../services/api';
import { toast } from 'react-toastify';
import ExportPDFButton from '../components/ExportPDFButton';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    mot_de_passe: '',
    role: 'consultation',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.getAll();
      setUsers(res.data.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingId(user.id);
      setFormData({
        prenom: user.prenom,
        nom: user.nom,
        email: user.email,
        mot_de_passe: '',
        role: user.role,
      });
    } else {
      setEditingId(null);
      setFormData({
        prenom: '',
        nom: '',
        email: '',
        mot_de_passe: '',
        role: 'consultation',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = { ...formData };
      if (!data.mot_de_passe) {
        delete data.mot_de_passe;
      }
      if (!data.telephone) {
        delete data.telephone;
      }

      if (editingId) {
        await usersAPI.update(editingId, data);
        toast.success('Utilisateur mis à jour');
      } else {
        await usersAPI.create(data);
        toast.success('Utilisateur créé');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await usersAPI.delete(deleteModal.id);
      toast.success('Utilisateur supprimé');
      setDeleteModal({ isOpen: false, id: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const columns = [
    { key: 'prenom', label: 'Prénom' },
    { key: 'nom', label: 'Nom' },
    { key: 'email', label: 'Email' },
    {
      key: 'role',
      label: 'Rôle',
      render: (val) => {
        const roles = {
          admin: 'Administrateur',
          agent: 'Agent',
          consultation: 'Consultation',
        };
        return <span className={`badge badge-${val === 'admin' ? 'error' : 'info'}`}>{roles[val] || val}</span>;
      },
    },
  ];

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="page-content" id="page-content">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="page-content" id="page-content">
          <div className="page-header">
            <h1 className="page-title">Utilisateurs</h1>
            <ExportPDFButton title="OGP IMMO - Utilisateurs" fileName="utilisateurs-ogp-immo" />
            <button onClick={() => handleOpenModal()} className="btn btn-secondary">
              <FiPlus /> Nouvel utilisateur
            </button>
          </div>

          <div className="card">
            <DataTable
              columns={columns}
              data={users}
              loading={loading}
              actions={(row) => [
                <button
                  key="edit"
                  onClick={() => handleOpenModal(row)}
                  className="btn btn-secondary btn-sm"
                  title="Éditer"
                >
                  <FiEdit2 />
                </button>,
                <button
                  key="delete"
                  onClick={() => setDeleteModal({ isOpen: true, id: row.id })}
                  className="btn btn-error btn-sm"
                  title="Supprimer"
                >
                  <FiTrash2 />
                </button>,
              ]}
            />
          </div>

          {/* Modal */}
          <Modal
            isOpen={showModal}
            title={editingId ? 'Éditer l\'utilisateur' : 'Nouvel utilisateur'}
            onClose={() => setShowModal(false)}
            footer={
              <>
                <button
                  onClick={() => setShowModal(false)}
                  className="btn btn-ghost"
                >
                  Annuler
                </button>
                <button onClick={handleSubmit} className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </>
            }
          >
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2" style={{ marginBottom: '1.5rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Prénom *</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    required
                    placeholder="Prénom"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Nom *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    placeholder="Nom"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="email@example.com"
                />
              </div>

              <div className="form-group">
                <label className="form-label">{editingId ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe * (10 caracteres minimum)'}</label>
                <input
                  type="password"
                  value={formData.mot_de_passe}
                  onChange={(e) => setFormData({ ...formData, mot_de_passe: e.target.value })}
                  required={!editingId}
                  placeholder="••••••••"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Rôle *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="consultation">Consultation</option>
                  <option value="agent">Agent</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
            </form>
          </Modal>

          {/* Delete Modal */}
          <Modal
            isOpen={deleteModal.isOpen}
            title="Confirmer la suppression"
            onClose={() => setDeleteModal({ isOpen: false, id: null })}
            footer={
              <>
                <button
                  onClick={() => setDeleteModal({ isOpen: false, id: null })}
                  className="btn btn-ghost"
                >
                  Annuler
                </button>
                <button onClick={handleDelete} className="btn btn-error">
                  Supprimer
                </button>
              </>
            }
          >
            <p>Êtes-vous sûr de vouloir supprimer cet utilisateur?</p>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default UsersPage;
